// src/components/Bidding/SaleAssets/TimedMetaRow.js
import React from 'react';
import { gql, useSubscription } from '@apollo/client';
import { Text } from 'src/styles';
import { dollars } from 'src/utils/stringHelpers';
import TimeRemaining from 'src/components/Timer';

const BIDDING_UPDATED = gql`
  subscription BiddingUpdated($saleId: ID!) {
    biddingUpdated(saleId: $saleId) {
      saleAssetId
      timestamp
      countdownTimeMs
      highBid
      __typename
    }
  }
`;

/**
 * Wrapper: no hooks here, so it can return early safely.
 */
export default function TimedMetaRow({ saleId, saleType, saleAsset }) {
  if (saleType !== 'Timed') return null;
  return <TimedMetaRowInner saleId={saleId} saleAsset={saleAsset} />;
}

/**
 * Inner: all hooks live here and are always called.
 */
function TimedMetaRowInner({ saleId, saleAsset }) {
  // seed from the query
  const [ms, setMs] = React.useState(
    Number.isFinite(saleAsset?.countdownTimeMs) ? Math.max(0, saleAsset.countdownTimeMs) : null
  );
  const [currentBid, setCurrentBid] = React.useState(saleAsset?.highBid ?? null);

  // keep in sync if parent query updates
  React.useEffect(() => {
    if (Number.isFinite(saleAsset?.countdownTimeMs)) {
      setMs(Math.max(0, saleAsset.countdownTimeMs));
    }
    if (saleAsset?.highBid != null) setCurrentBid(saleAsset.highBid);
  }, [saleAsset?.id, saleAsset?.countdownTimeMs, saleAsset?.highBid]);

  // live updates (anchor to server timestamp to avoid drift)
  useSubscription(BIDDING_UPDATED, {
    variables: { saleId },
    onData: ({ data }) => {
      const p = data?.data?.biddingUpdated;
      if (!p || p.saleAssetId !== saleAsset?.id) return;

      if (typeof p.countdownTimeMs === 'number') {
        const asOf = Number(p.timestamp) || Date.now();
        const adjusted = Math.max(0, p.countdownTimeMs - (Date.now() - asOf));
        setMs(adjusted);
      }
      if (p.highBid != null) setCurrentBid(String(p.highBid));
    },
  });

  return (
    <div style={{ marginTop: 6 }}>
      <Text style={{ fontWeight: 600 }}>
        Current Bid:{' '}
        {currentBid ? dollars(currentBid) : '$0'}
        {'  '}
        <span style={{ opacity: 0.6 }}>•</span>{' '}
        Time Remaining:{' '}
        <TimeRemaining remainingMs={ms} />
      </Text>
    </div>
  );
}
